// Transposición de acordes en formato ChordPro: "[G]Sublime [D/F#]gracia".

const SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
const FLATS = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'] as const;

/** Tonalidades seleccionables en la interfaz. */
export const KEY_OPTIONS = ['C', 'C#', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'] as const;

/** Tonalidades mayores que se escriben con bemoles. */
const FLAT_MAJOR_KEYS = new Set(['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb']);
/** Tonalidades menores que se escriben con bemoles (por su relativa mayor). */
const FLAT_MINOR_KEYS = new Set(['D', 'G', 'C', 'F', 'Bb', 'Eb']);

/** Índice 0-11 de una nota, o null si no es nota. Acepta "C#", "Db", "E#" no. */
export function noteIndex(note: string): number | null {
  const s = SHARPS.indexOf(note as (typeof SHARPS)[number]);
  if (s >= 0) return s;
  const f = FLATS.indexOf(note as (typeof FLATS)[number]);
  if (f >= 0) return f;
  // Enarmónicos poco comunes
  const extra: Record<string, number> = { Cb: 11, 'B#': 0, Fb: 4, 'E#': 5 };
  return note in extra ? extra[note]! : null;
}

/** Interpreta una tonalidad: "G" -> {root:"G", minor:false}; "Em" -> {root:"E", minor:true}. */
export function parseKey(key: string): { root: string; minor: boolean } | null {
  const m = /^([A-G](?:#|b)?)(m|min|minor)?$/.exec((key || '').trim());
  if (!m || noteIndex(m[1]!) === null) return null;
  return { root: m[1]!, minor: Boolean(m[2]) };
}

/** ¿Son la misma tonalidad? Considera enarmónicos (F# = Gb) y distingue mayor de menor. */
export function sameKey(a: string, b: string): boolean {
  const x = parseKey(a);
  const y = parseKey(b);
  return !!x && !!y && x.minor === y.minor && noteIndex(x.root) === noteIndex(y.root);
}

/** ¿La tonalidad destino se escribe con bemoles? */
export function usesFlats(key: string): boolean {
  const k = parseKey(key);
  if (!k) return false;
  if (k.root.includes('b')) return true;
  return k.minor ? FLAT_MINOR_KEYS.has(k.root) : FLAT_MAJOR_KEYS.has(k.root);
}

/** Semitonos para ir de una tonalidad a otra (0-11). */
export function semitonesBetween(fromKey: string, toKey: string): number {
  const a = parseKey(fromKey);
  const b = parseKey(toKey);
  if (!a || !b) throw new Error(`Tonalidad inválida: ${!a ? fromKey : toKey}`);
  return (((noteIndex(b.root)! - noteIndex(a.root)!) % 12) + 12) % 12;
}

function transposeNote(note: string, semis: number, flats: boolean): string {
  const i = noteIndex(note);
  if (i === null) return note;
  const j = (((i + semis) % 12) + 12) % 12;
  return (flats ? FLATS : SHARPS)[j]!;
}

/**
 * Transpone un acorde: "D/F#" +2 -> "E/G#"; "Bbmaj7" +2 -> "Cmaj7"; "N.C." queda igual.
 * El sufijo (m7, sus4, add9, b5...) se conserva tal cual.
 */
export function transposeChord(chord: string, semis: number, flats: boolean): string {
  const m = /^([A-G](?:#|b)?)(.*)$/.exec(chord);
  if (!m) return chord;
  const root = m[1]!;
  let rest = m[2]!;
  let bass = '';
  const slash = /^(.*)\/([A-G](?:#|b)?)$/.exec(rest);
  if (slash) {
    rest = slash[1]!;
    bass = '/' + transposeNote(slash[2]!, semis, flats);
  }
  return transposeNote(root, semis, flats) + rest + bass;
}

/** Transpone todos los acordes [entre corchetes] de un texto ChordPro. Las directivas {…} no se tocan. */
export function transposeChordPro(text: string, fromKey: string, toKey: string): string {
  const semis = semitonesBetween(fromKey, toKey);
  const flats = usesFlats(toKey);
  if (semis === 0 && usesFlats(fromKey) === flats) return text;
  return text.replace(/\[([^\]\n]+)\]/g, (_all, chord: string) => `[${transposeChord(chord.trim(), semis, flats)}]`);
}

/** Quita acordes y directivas de un ChordPro y deja solo la letra. */
export function chordProToLyrics(text: string): string {
  return text
    .split('\n')
    .filter((line) => !/^\s*\{.*\}\s*$/.test(line))
    .map((line) => line.replace(/\[[^\]\n]*\]/g, '').replace(/\s+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export interface ChordLineSegment {
  chord: string | null;
  lyric: string;
}

/** Divide una línea ChordPro en segmentos acorde+letra para dibujar el acorde sobre la sílaba. */
export function splitChordLine(line: string): ChordLineSegment[] {
  const segments: ChordLineSegment[] = [];
  const re = /\[([^\]\n]*)\]/g;
  let last = 0;
  let pendingChord: string | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) {
    const lyric = line.slice(last, m.index);
    if (lyric || pendingChord !== null) segments.push({ chord: pendingChord, lyric });
    pendingChord = m[1]!;
    last = m.index + m[0].length;
  }
  const tail = line.slice(last);
  if (tail || pendingChord !== null) segments.push({ chord: pendingChord, lyric: tail });
  return segments;
}

/** Nota escrita como una de KEY_OPTIONS (A# → Bb, D# → Eb, G# → Ab, Cb → B…), o null si no es nota. */
export function toKeyOption(note: string): (typeof KEY_OPTIONS)[number] | null {
  const n = noteIndex(note.trim());
  if (n === null) return null;
  if ((KEY_OPTIONS as readonly string[]).includes(note.trim())) return note.trim() as (typeof KEY_OPTIONS)[number];
  const byIndex = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'] as const;
  return byIndex[n]!;
}

const CHORD_TOKEN = /^\(?[A-G](?:#|b)?(?:m(?!aj)|min|maj|M|dim|aug|sus|add|°|ø|\+)?[0-9]*(?:(?:sus|add|maj|b|#)[0-9]+)*(?:\([^)\s]*\))?(?:\/[A-G](?:#|b)?)?\)?$/;
// Lo que puede ir entre acordes en una línea de acordes: barras de compás, guiones, repeticiones.
const CHORD_LINE_FILLER = /^(\||\|\||-|–|\/|%|\.+|x\d+|\(x\d+\)|\d+x|\(\d+x\))$/i;

/** ¿Es una línea solo de acordes («C   D  Em», «| A - C#m |»)? */
export function isChordLine(line: string): boolean {
  const tokens = line.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return false;
  let chords = 0;
  for (const t of tokens) {
    if (CHORD_TOKEN.test(t)) chords++;
    else if (!CHORD_LINE_FILLER.test(t)) return false;
  }
  return chords > 0;
}

/** ¿El texto trae acordes, ya sea [en corchetes] (ChordPro) o en líneas encima de la letra? */
export function hasChords(text: string): boolean {
  return /\[[A-G](?:#|b)?[^\]\n]{0,10}\]/.test(text) || text.split(/\r?\n/).some(isChordLine);
}

/**
 * Convierte una partitura con los acordes en la línea de arriba (como se escribe en Word o en
 * Planning Center) a ChordPro, con cada acorde [entre corchetes] justo antes de su sílaba:
 *
 *   C        D     Em
 *   Tú eres el principio      →   [C]Tú eres el [D]princi[Em]pio
 *
 * Una línea de acordes sin letra debajo (intro, interludio) queda como «[C] [D] [Em]».
 * El texto que ya está en ChordPro no cambia.
 */
export function chordsOverLyricsToChordPro(text: string): string {
  const lines = text.replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n');
  const out: string[] = [];
  const bracket = (t: string) => (CHORD_TOKEN.test(t) ? `[${t.replace(/^\(|\)$/g, '')}]` : t);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (!isChordLine(line)) {
      out.push(line);
      continue;
    }
    const next = lines[i + 1];
    const lyricBelow = next !== undefined && next.trim() !== '' && !isChordLine(next) && !/\[[^\]\n]+\]/.test(next) && !/^\s*\{.*\}\s*$/.test(next);
    if (!lyricBelow) {
      out.push(line.trim().split(/\s+/).map(bracket).join(' '));
      continue;
    }
    // Posición de cada acorde en la línea de arriba → se inserta en la misma columna de la letra.
    const chords: { col: number; chord: string }[] = [];
    for (const m of line.matchAll(/\S+/g)) if (CHORD_TOKEN.test(m[0])) chords.push({ col: m.index!, chord: m[0].replace(/^\(|\)$/g, '') });
    let lyric = next!.replace(/\s+$/, '');
    const width = Math.max(...chords.map((c) => c.col));
    if (lyric.length < width) lyric = lyric.padEnd(width, ' ');
    for (const c of [...chords].reverse()) lyric = `${lyric.slice(0, c.col)}[${c.chord}]${lyric.slice(c.col)}`;
    out.push(lyric.replace(/\s+$/, ''));
    i++; // la letra ya se usó
  }
  return out.join('\n');
}

/** Una línea ChordPro con sus acordes arriba, o null si así cambiarían de lugar o se mezclarían con la línea siguiente. */
function lineToChordsOverLyrics(line: string, next: string | undefined): string[] | null {
  const chords: { col: number; chord: string }[] = [];
  let lyric = '';
  let last = 0;
  for (const m of line.matchAll(/\[([^\]\n]+)\]/g)) {
    lyric += line.slice(last, m.index);
    chords.push({ col: lyric.length, chord: m[1]! });
    last = m.index! + m[0].length;
  }
  if (!chords.length) return [line];
  lyric = (lyric + line.slice(last)).replace(/\s+$/, '');
  const target = line.replace(/\s+$/, '');
  const onlyChords = line.replace(/\[[^\]\n]+\]/g, '').split(/\s+/).every((t) => !t || CHORD_LINE_FILLER.test(t));
  if (onlyChords) {
    // Sin letra: si la línea siguiente es letra, al guardar los acordes se le pegarían.
    const nextIsLyric = next !== undefined && next.trim() !== '' && !isChordLine(next) && !/\[[^\]\n]+\]/.test(next) && !/^\s*\{.*\}\s*$/.test(next);
    if (nextIsLyric) return null;
    const over = line.replace(/\[([^\]\n]+)\]/g, '$1').trim();
    return chordsOverLyricsToChordPro(over) === target ? [over] : null;
  }
  let chordLine = '';
  for (const c of chords) {
    if (chordLine && c.col <= chordLine.length) return null; // Dos acordes demasiado juntos.
    chordLine = chordLine.padEnd(c.col, ' ') + c.chord;
  }
  return chordsOverLyricsToChordPro(`${chordLine}\n${lyric}`) === target ? [chordLine, lyric] : null;
}

/**
 * Lo contrario de chordsOverLyricsToChordPro, para editar como en Planning Center. Una línea que
 * no se puede escribir así sin mover sus acordes queda en ChordPro. Si el resultado no vuelve
 * exactamente al mismo texto al convertirlo de nuevo, se devuelve el original sin cambios.
 */
export function chordProToChordsOverLyrics(text: string): string {
  const clean = text.replace(/\r\n?/g, '\n');
  const lines = clean.split('\n');
  const out = lines.flatMap((line, i) => lineToChordsOverLyrics(line, lines[i + 1]) ?? [line]);
  const result = out.join('\n');
  const trimLines = (v: string) => v.split('\n').map((l) => l.replace(/\s+$/, '')).join('\n');
  return trimLines(chordsOverLyricsToChordPro(result)) === trimLines(clean) ? result : text;
}
